/**
 * Optimized Report Data Models
 * Refactored to avoid deep access patterns like modelProperties.modelProperties
 */

// ============================================
// CORE PROJECT INFO
// ============================================

export interface ProjectInfo {
  projectId: number;
  projectName: string;
  projectCode: string;
  reportGenerated: string;
  softwareVersion: string;
  organizationName: string;
  organizationLocation: string;
  organizationLogo?: string;
  generatedBy: string;
  authors: string;
  selectedDefaultLocation: string;
  functionalUnitName: string;
  functionalUnitId: number;
  baseScenarioId: number;
  baseScenarioName: string;
  recommendationsAndConclusions: string;
}

export interface AuthorInfo {
  userId: number;
  userName: string;
  userCountry: string;
  organizationId: number;
  organizationName: string;
  organizationCountry: string;
}

export interface ReviewerInfo extends AuthorInfo {}

// ============================================
// SCENARIO MODELS
// ============================================

export interface ScenarioInfo {
  scenarioId: number;
  scenarioName: string;
  shortScenarioName: string;
  scenarioImage?: string;
}

export interface PackagingLevel {
  packageLevelInfoId: number;
  packagingLevelId: number;
  name: string;
  amount: number;
  totalUnitsPerPallet: number;
  details: string;
}

export interface ComponentTransport {
  transportMode: string;
  fromPlace: string;
  toPlace: string;
  distance: string;
}

export interface PackagingComponent {
  componentName: string;
  packagingLevelName: string;
  weight: number;
  materialName: string;
  recycledContent: string;
  conversionProcessName: string;
  conversionSupplierName: string;
  materialSupplierName: string;
  materialCountryName: string;
  conversionCountryName: string;
  recycledAllocationFactor: string;
  componentTransports: ComponentTransport[];
  fillingTransports: ComponentTransport[];
}

export interface EndOfLifeItem {
  label: string;
  value: string;
}

export interface EndOfLifeComponent {
  componentName: string;
  items: EndOfLifeItem[];
}

export interface FillerTransport {
  componentName: string;
  transports: ComponentTransport[];
}

export interface ScenarioDetail {
  scenarioId: number;
  scenarioName: string;
  packagingLevels: PackagingLevel[];
  components: PackagingComponent[];
  fillerTransports: FillerTransport[];
  endOfLifeComponents: EndOfLifeComponent[];
  wholesaleItems: { label: string; value: string }[];
  retailItems: { label: string; value: string }[];
  consumerItems: { label: string; value: string }[];
}

// ============================================
// IMPACT CATEGORIES
// ============================================

export interface ImpactIndicator {
  id: number;
  name: string;
  unit: string;
  description: string;
  underlyingModel: string;
}

export interface MaterialCertificationItem {
  group: string;
  criteria: string;
  values: string[];
}

export interface MaterialCertification {
  components: string[];
  items: MaterialCertificationItem[];
}

// ============================================
// GOAL AND SCOPE
// ============================================

export interface SystemBoundaryProcess {
  name: string;
  description: string;
  selected: boolean;
  flag: boolean;
}

export interface GoalAndScope {
  goal: string;
  functionalUnit: string;
  functionalUnitId: number;
  functionalUnitText: string;
  systemBoundaryImage: string;
  systemBoundaryJustification: string;
  multiFunctionalDescription: string;
  otherAssumptions: string;
  cutOffPercentage: string;
  excludedProcessesJustification: string;
  impactCategoryJustification: string;
  sensitivityAnalysisDescription: string;
  geographicalBoundary: string;
  temporalBoundary: string;
  approachToBiogenicCarbonDesc: string;
  uncertaintyAnalysisDescription: string;
  introductionToISOReport: string;
  includedProcesses: SystemBoundaryProcess[];
  packagingLevelProcesses: SystemBoundaryProcess[];
}

// ============================================
// INTRODUCTION
// ============================================

export interface Introduction {
  organizationName: string;
  productNames: string;
  selectedScenarioNames: string[];
  lcaFrameworkImage: string;
}

// ============================================
// SHOW/HIDE SECTIONS
// ============================================

export interface SectionVisibility {
  coverPage: boolean;
  introduction: boolean;
  goalAndScope: boolean;
  inventoryAnalysis: boolean;
  impactAssessment: boolean;
  interpretation: boolean;
  conclusion: boolean;
}

// ============================================
// MAIN REPORT DATA (OPTIMIZED)
// ============================================

export interface OptimizedReportData {
  projectInfo: ProjectInfo;
  authors: AuthorInfo[];
  reviewers: ReviewerInfo[];
  scenarios: ScenarioInfo[];
  scenarioDetails: ScenarioDetail[];
  indicators: ImpactIndicator[];
  materialCertification: MaterialCertification;
  goalAndScope: GoalAndScope;
  introduction: Introduction;
  sectionVisibility: SectionVisibility;
  reportFormatId: number;
  isExcludeProductImpacts: boolean;
  isMaterialCertificationRights: boolean;
  isAppendixMaterialCategoriesRights: boolean;
}

// ============================================
// DATA TRANSFORMER (Raw to Optimized)
// ============================================

export class ReportDataTransformer {
  /**
   * Transform raw API response to optimized data model
   */
  static transform(rawData: any): OptimizedReportData {
    const data = rawData.data || rawData;

    return {
      projectInfo: this.transformProjectInfo(data.common),
      authors: this.transformAuthors(data.common?.authorsInfoList || []),
      reviewers: this.transformAuthors(data.common?.reviewerInfoList || []),
      scenarios: this.transformScenarios(data.common?.scenarioNameList || [], data.common?.scenarioImages || []),
      scenarioDetails: this.transformScenarioDetails(data.scenarioDetailItemList || []),
      indicators: this.transformIndicators(data.common?.indicatorList || []),
      materialCertification: this.transformMaterialCertification(data.materialCertificateImpact),
      goalAndScope: this.transformGoalAndScope(data.goalAndScope),
      introduction: this.transformIntroduction(data.introduction),
      sectionVisibility: this.transformSectionVisibility(data.showHideSection),
      reportFormatId: data.reportFormatId || 0,
      isExcludeProductImpacts: data.isExcludeProductImpacts || false,
      isMaterialCertificationRights: data.isMaterialCertificationRights || false,
      isAppendixMaterialCategoriesRights: data.isAppendixMaterialCategoriesRights || false,
    };
  }

  private static transformProjectInfo(common: any): ProjectInfo {
    if (!common) return {} as ProjectInfo;

    return {
      projectId: common.projectId || 0,
      projectName: common.projectName || '',
      projectCode: common.projectCode || '',
      reportGenerated: common.reportGenerated || '',
      softwareVersion: common.softwareVersion || '',
      organizationName: common.organizationName || '',
      organizationLocation: common.organizationLocation || '',
      organizationLogo: common.organizationLogo || '',
      generatedBy: common.generatedBy || '',
      authors: common.authors || '',
      selectedDefaultLocation: common.selectedDefaultLocation || '',
      functionalUnitName: common.functionalUnitName || '',
      functionalUnitId: common.functionalUnitId || 0,
      baseScenarioId: common.baseScenarioId || 0,
      baseScenarioName: common.baseScenarioName || '',
      recommendationsAndConclusions: common.recommendationsAndConclusions || '',
    };
  }

  private static transformAuthors(list: any[]): AuthorInfo[] {
    return list.map(item => ({
      userId: item.userId || 0,
      userName: item.userName || '',
      userCountry: item.userCountry || '',
      organizationId: item.organizationId || 0,
      organizationName: item.organizationName || '',
      organizationCountry: item.organizationCountry || '',
    }));
  }

  private static transformScenarios(nameList: any[], images: string[]): ScenarioInfo[] {
    return nameList.map((item, index) => ({
      scenarioId: index + 1,
      scenarioName: item.scenarioName || '',
      shortScenarioName: item.shortScenarioName || '',
      scenarioImage: images[index] || '',
    }));
  }

  private static transformScenarioDetails(list: any[]): ScenarioDetail[] {
    return list.map(item => ({
      scenarioId: item.scenarioId || 0,
      scenarioName: item.scenarioName || '',
      packagingLevels: this.transformPackagingLevels(item.packagingLevelItems || []),
      components: this.transformComponents(item.componentItems || []),
      fillerTransports: this.transformFillerTransports(item.fillertransportationItems || []),
      endOfLifeComponents: this.transformEndOfLife(item.endoflifeIdentifierItems || []),
      wholesaleItems: item.wholesaleItems || [],
      retailItems: item.retailItems || [],
      consumerItems: item.consumerItems || [],
    }));
  }

  private static transformPackagingLevels(list: any[]): PackagingLevel[] {
    return list.map(item => ({
      packageLevelInfoId: item.packageLevelInfoId || 0,
      packagingLevelId: item.packagingLevelId || 0,
      name: item.name || '',
      amount: item.amount || 0,
      totalUnitsPerPallet: item.totalunitsperPallet || 0,
      details: item.packageLevelDetails || '',
    }));
  }

  private static transformComponents(list: any[]): PackagingComponent[] {
    return list.map(item => ({
      componentName: item.componentName || '',
      packagingLevelName: item.packagingLevelName || '',
      weight: item.weight || 0,
      materialName: item.materialName || '',
      recycledContent: item.recycledContent || '0',
      conversionProcessName: item.conversionProcessName || 'N/A',
      conversionSupplierName: item.conversionSupplierName || 'N/A',
      materialSupplierName: item.materialSupplierName || 'N/A',
      materialCountryName: item.materialCountryName || '',
      conversionCountryName: item.conversionCountryName || '',
      recycledAllocationFactor: item.recycledAllocationFactor || '50',
      componentTransports: this.transformTransports(item.componenttransportationItems || []),
      fillingTransports: this.transformTransports(item.fillingtransportationItems || []),
    }));
  }

  private static transformTransports(list: any[]): ComponentTransport[] {
    return list.map(item => ({
      transportMode: item.transportmodeName || '',
      fromPlace: item.fromplace || 'N/A',
      toPlace: item.toplace || 'N/A',
      distance: item.distance || '0',
    }));
  }

  private static transformFillerTransports(list: any[]): FillerTransport[] {
    return list.map(item => ({
      componentName: item.componentName || '',
      transports: this.transformTransports(item.fillingtransportationItems || []),
    }));
  }

  private static transformEndOfLife(list: any[]): EndOfLifeComponent[] {
    return list.map(item => ({
      componentName: item.componentName || '',
      items: item.endoflifeIdentifierItems || [],
    }));
  }

  private static transformIndicators(list: any[]): ImpactIndicator[] {
    return list.map(item => ({
      id: item.id || 0,
      name: item.name || '',
      unit: item.unit || '',
      description: item.description || '',
      underlyingModel: item.underlyingCharacterizationModel || '',
    }));
  }

  private static transformMaterialCertification(data: any): MaterialCertification {
    if (!data) return { components: [], items: [] };

    return {
      components: data.components || [],
      items: (data.list || []).map((item: any) => ({
        group: item.group || '',
        criteria: item.criteria || '',
        values: item.values || [],
      })),
    };
  }

  private static transformGoalAndScope(data: any): GoalAndScope {
    if (!data) return {} as GoalAndScope;

    return {
      goal: data.goal || '',
      functionalUnit: data.functionalUnit || '',
      functionalUnitId: data.functionalUnitId || 0,
      functionalUnitText: data.functionalUnitText || '',
      systemBoundaryImage: data.systemBoundaryImage || '',
      systemBoundaryJustification: data.systemBoundaryJustification || '',
      multiFunctionalDescription: data.multiFunctionalDescription || '',
      otherAssumptions: data.otherAssumptions || '',
      cutOffPercentage: data.cutOffPercentage || '',
      excludedProcessesJustification: data.excludedProcessesJustification || '',
      impactCategoryJustification: data.impactCategoryJustification || '',
      sensitivityAnalysisDescription: data.sensitivityAnalysisDescription || '',
      geographicalBoundary: data.geographicalBoundary || '',
      temporalBoundary: data.temporalBoundary || '',
      approachToBiogenicCarbonDesc: data.approachToBiogenicCarbonDesc || '',
      uncertaintyAnalysisDescription: data.uncertaintyAnalysisDescription || '',
      introductionToISOReport: data.introductionToISOReport || '',
      includedProcesses: this.transformProcesses(data.includeProcessHideShowDetailModel || []),
      packagingLevelProcesses: [],
    };
  }

  private static transformProcesses(list: any[]): SystemBoundaryProcess[] {
    return list.map(item => ({
      name: item.name || '',
      description: item.description || '',
      selected: item.selected || false,
      flag: item.flag || false,
    }));
  }

  private static transformIntroduction(data: any): Introduction {
    if (!data) return {} as Introduction;

    return {
      organizationName: data.organizationName || '',
      productNames: data.productNames || '',
      selectedScenarioNames: (data.selectedScenarioName || '').split(';').filter(Boolean),
      lcaFrameworkImage: data.lcaFrameworkImage || '',
    };
  }

  private static transformSectionVisibility(data: any): SectionVisibility {
    if (!data) {
      return {
        coverPage: true,
        introduction: true,
        goalAndScope: true,
        inventoryAnalysis: true,
        impactAssessment: true,
        interpretation: true,
        conclusion: true,
      };
    }

    return {
      coverPage: data.isCoverPage ?? true,
      introduction: data.isIntroduction ?? true,
      goalAndScope: data.isGoalAndScope ?? true,
      inventoryAnalysis: data.isInventoryAnalysis ?? true,
      impactAssessment: data.isImpactAssessment ?? true,
      interpretation: data.isInterpretation ?? true,
      conclusion: data.isConclusion ?? true,
    };
  }
}
